import type { Assessment } from "@repo/app-types/entities/assessment";
import {
  CANDIDATE_ALIGN_FIELD_NAME,
  CefrAssessmentFieldNameToLanguage,
  sortAssessmentFields,
} from "@repo/app-types/entities/assessment";
import { CustomFieldDefinitionImportanceSetting } from "@repo/app-types/entities/customField";
import type { ProjectWithInterviewCount } from "@repo/app-types/entities/project";
import {
  PermissionActions,
  PermissionSubjects,
} from "@repo/app-types/entities/teammatePermissions";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { IconButton } from "@repo/ui/Buttons/iconButton";
import { Size } from "@repo/ui/helpers/helpers";
import { Label } from "@repo/ui/Label/label";
import { Loader, LoaderStyle } from "@repo/ui/Loader/loader";
import { TooltipHoverTarget } from "@repo/ui/tooltip/hoverTarget";
import { SimpleTooltip } from "@repo/ui/tooltip/simpleTooltip";
import { TooltipTrigger } from "@repo/ui/tooltip/tooltipTrigger";
import { IconLanguage, IconRefresh, IconUser } from "@tabler/icons-react";
import type { FC } from "react";
import { OverviewBox } from "../../components/overviewBox.js";
import { ReadOnlyBulletedList } from "../../components/readOnlyBulletedList.js";
import { Can } from "../../helpers/teammateAuthorizationContext.js";
import {
  renderAssessmentSettingsLabel,
  renderCustomFieldDefinitionImportanceMustHaveIcon,
  renderCustomFieldDefinitionImportanceOther,
} from "./helpers.js";

export interface AssessmentSettingsOverviewProps {
  project: ProjectWithInterviewCount;
  assessment: Assessment | undefined;
  isDisabled: boolean;
  isLoading: boolean;
  isFailed: boolean;
  onRetryClick: () => void;
  onEditClick: () => void;
}

const NUM_CRITERIA_TO_DISPLAY = 4;

/** Used to preview the assessment on the project detail/settings page. */
export const AssessmentSettingsOverview: FC<AssessmentSettingsOverviewProps> = (
  props,
) => {
  const { project, assessment } = props;
  const renderLoadedContent = () => {
    // Show something only if an assessment exists.
    if (!assessment) {
      return null;
    }

    // Criteria items will only be displayed for recruiting projects with an assessment.
    const sortedFields = sortAssessmentFields(
      assessment.custom_field_definitions,
    );
    const criteriaItems = sortedFields.map((field) => field.display_name);

    // Create an icon function that returns the appropriate icon based on field importance
    const renderIcon = (_item: string, index: number) => {
      const field = sortedFields[index];
      if (field === undefined) {
        return null;
      }

      // CEFR field
      if (field.field_name in CefrAssessmentFieldNameToLanguage) {
        return (
          <div className="flex items-center gap-2">
            <IconLanguage className="h-4 w-4" />
          </div>
        );
      }

      // Candidate align field
      if (field.display_name === CANDIDATE_ALIGN_FIELD_NAME) {
        return (
          <div className="flex items-center gap-2">
            <IconUser className="h-4 w-4" />
          </div>
        );
      }

      const isMustHave =
        field.settings?.importance ===
        CustomFieldDefinitionImportanceSetting.MustHave;

      const tooltipContent = isMustHave
        ? "Qualification is a must have requirement"
        : "Qualification is not a must have requirement";

      return (
        <div className="flex items-center gap-2">
          <TooltipTrigger>
            <TooltipHoverTarget shape="rounded">
              {isMustHave
                ? renderCustomFieldDefinitionImportanceMustHaveIcon()
                : renderCustomFieldDefinitionImportanceOther()}
            </TooltipHoverTarget>
            <SimpleTooltip>{tooltipContent}</SimpleTooltip>
          </TooltipTrigger>
        </div>
      );
    };

    // If there are no criteria items, show an empty state.
    if (criteriaItems.length === 0) {
      return (
        <div className="text-sm text-muted-foreground">
          <Can
            I={PermissionActions.UPDATE}
            a={PermissionSubjects.JOB_OPENINGS}
            passThrough={true}
          >
            {(allowed) => (
              <Button
                variant={ButtonVariant.Primary}
                onClick={props.onEditClick}
                isDisabled={!allowed}
              >
                Add assessment criteria
              </Button>
            )}
          </Can>
        </div>
      );
    }

    return (
      <OverviewBox isDisabled={props.isDisabled} onClick={props.onEditClick}>
        <ReadOnlyBulletedList
          items={criteriaItems}
          maxItemsToDisplay={NUM_CRITERIA_TO_DISPLAY}
          itemDescriptor="criteria"
          icon={renderIcon}
        />
      </OverviewBox>
    );
  };

  const renderContent = () => {
    const loadedContent = renderLoadedContent();
    if (loadedContent) {
      return loadedContent;
    }

    if (props.isLoading) {
      return (
        <div className="flex items-center space-x-2 text-sm text-foreground">
          <span className="size-6 shrink-0">
            <Loader style={LoaderStyle.ORBIT} />
          </span>
          <span>
            Your assessment is being generated. This may take a minute.
          </span>
        </div>
      );
    }

    if (props.isFailed) {
      return (
        <div className="flex items-center space-x-2 text-sm text-foreground">
          <IconButton
            variant={ButtonVariant.Tertiary}
            icon={<IconRefresh className="size-4 shrink-0" />}
            onClick={props.onRetryClick}
          />
          <span>An error occurred while generating your assessment.</span>
        </div>
      );
    }

    return null;
  };

  const content = renderContent();
  if (!content) {
    return null;
  }

  return (
    <div>
      <div className="mb-2">
        <Label size={Size.SMALL}>{renderAssessmentSettingsLabel()}</Label>
      </div>
      {content}
      <div className="mt-2 text-sm text-gray-600">
        Starred qualifications are a &quot;must have&quot; requirement
      </div>
    </div>
  );
};
